"use client";

import * as React from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../primitives/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../primitives/alert-dialog";
import { Button } from "../inputs/button";

export interface FormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /**
   * Keep `title` for the dialog's accessible name but hide it visually —
   * used when the first field (e.g. a template picker) is the header.
   */
  hideTitle?: boolean;
  description?: string;
  onSubmit: () => Promise<void>;
  onError?: (error: Error) => void;
  confirmText?: string;
  loadingText?: string;
  cancelText?: string;
  onCancel?: () => void;
  closeOnSubmit?: boolean;
  variant?: "default" | "destructive";
  children: React.ReactNode;
  /** Shown in place of the form once it has done its job, inside the same open dialog. */
  completed?: React.ReactNode;
  disabled?: boolean;
  /** Extra class names forwarded to DialogContent (e.g. to override max-width). */
  className?: string;
  /**
   * True while the form holds unsaved input. When set, an accidental dismissal
   * — click outside, Esc, or the header ✕ — asks before discarding instead of
   * throwing the work away. The Cancel button stays immediate: pressing it is
   * explicit intent to abandon the form.
   */
  isDirty?: boolean;
  /** Overrides the discard prompt's body copy. */
  discardDescription?: string;
}

export function FormDialog({
  open,
  onOpenChange,
  title,
  hideTitle = false,
  description,
  onSubmit,
  onError,
  confirmText = "Save",
  loadingText,
  cancelText = "Cancel",
  onCancel,
  closeOnSubmit = true,
  variant = "default",
  children,
  completed,
  disabled = false,
  className,
  isDirty = false,
  discardDescription = "Your changes to this form haven't been saved. Closing now discards them.",
}: FormDialogProps) {
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [discardOpen, setDiscardOpen] = React.useState(false);

  // Callers can close via the controlled `open` prop (success, reactivate,
  // etc.) while the discard prompt is up — clear it so it can't outlive us.
  React.useEffect(() => {
    if (!open) setDiscardOpen(false);
  }, [open]);

  // Radix routes outside-click, Esc, and the ✕ through onOpenChange. Ignore
  // dismissal while submission is in flight, then guard unsaved input normally.
  function handleOpenChange(next: boolean) {
    if (!next && isSubmitting) return;
    if (!next && isDirty) {
      setDiscardOpen(true);
      return;
    }
    onOpenChange(next);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onSubmit();
      if (closeOnSubmit) onOpenChange(false);
    } catch (err) {
      const normalized = err instanceof Error ? err : new Error(String(err));
      if (onError) {
        onError(normalized);
      } else {
        console.error("[FormDialog] submit failed:", normalized);
        toast.error(normalized.message || "Something went wrong");
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-slot="form-dialog"
        className={["max-h-[calc(100svh-2rem)] overflow-hidden", className]
          .filter(Boolean)
          .join(" ")}
        {...(!description ? { "aria-describedby": undefined } : {})}
      >
        {completed ? (
          completed
        ) : (
          <form onSubmit={handleSubmit} className="flex min-h-0 min-w-0 flex-col">
            {hideTitle && !description ? (
              <DialogTitle className="sr-only">{title}</DialogTitle>
            ) : (
              <DialogHeader>
                <DialogTitle className={hideTitle ? "sr-only" : undefined}>{title}</DialogTitle>
                {description && <DialogDescription>{description}</DialogDescription>}
              </DialogHeader>
            )}
            <div
              className={
                hideTitle
                  ? "min-h-0 min-w-0 overflow-y-auto pb-4"
                  : "min-h-0 min-w-0 overflow-y-auto py-4"
              }
            >
              {children}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={onCancel ?? (() => onOpenChange(false))}
                disabled={isSubmitting}
              >
                {cancelText}
              </Button>
              <Button type="submit" variant={variant} disabled={disabled || isSubmitting}>
                {isSubmitting ? (loadingText ?? `${confirmText}...`) : confirmText}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
      {/* Rendered inside <Dialog> so the form stays mounted (and its state
          intact) behind the prompt — dismissing the prompt returns the user to
          exactly what they were typing. */}
      <AlertDialog open={discardOpen} onOpenChange={setDiscardOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard changes?</AlertDialogTitle>
            <AlertDialogDescription>{discardDescription}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setDiscardOpen(false);
                onOpenChange(false);
              }}
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
